import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, Download, Mail, Phone, MapPin, Globe, Instagram,
  Compass, PencilRuler, Sparkles, Gem, Wrench, Layers, Eye, ShieldCheck,
  Home, Building2, Sofa, Hammer, Quote, Star,
} from "lucide-react";
import { RevealText, FadeUp } from "@/components/RevealText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const A = "https://customer-assets-v7afamib.emergentagent.net/job_studi-hauz-profile/artifacts";

const IMG = {
  hero: `${A}/unuwvuto_1dc99965-a063-425a-b8fb-17475576e490.jpg`,
  bedroom: `${A}/c6067iwo_4f88145b-8f81-4d89-8dc3-ee3522344ccc.jpg`,
  studio: `${A}/vibpnkmz_0cabafdd-b72c-4803-80e5-fca155ef81c6.jpg`,
  dining: `${A}/we6l9vnx_01ba197a-948e-4c95-9888-2f1605842d31.jpg`,
  desk: `${A}/n0b9k0xp_1f3f135e-4900-478e-beab-fb89c90641b5.jpg`,
  render: `${A}/mcnypkya_54ab85be-9367-49f5-8ebb-6fbcbe7e1d12.jpg`,
  living2: `${A}/l9chzwv9_52d3b579-3ed3-4ffe-9cf5-7e39a1746caa.jpg`,
  processBath: `${A}/4wz6taoy_360ed3b3-2705-4701-90a5-80eebbd00548.jpg`,
  floorInlay: `${A}/ulmuiio5_6214476b-03f1-4916-af51-0cd3e1454645.jpg`,
  heroVideo: `${A}/c29u1ujg_IMG_2485.mp4`,
  bedroomRender: `${A}/nx03zvs3_WhatsApp%20Image%202026-07-22%20at%2001.43.55%20%281%29.jpeg`,
  bar: `${A}/yepgc3eb_WhatsApp%20Image%202026-07-22%20at%2001.43.58.webp`,
  diningClassic: `${A}/tkhdtru1_WhatsApp%20Image%202026-07-22%20at%2001.43.57%20%281%29.webp`,
  bedroomClassic: `${A}/ivpzpfpn_WhatsApp%20Image%202026-07-22%20at%2001.43.57.webp`,
  marbleInlay: `${A}/ioc2jtxu_WhatsApp%20Image%202026-07-22%20at%2001.43.58%20%281%29.webp`,
  barRender: `${A}/3wpcn83m_WhatsApp%20Image%202026-07-22%20at%2001.43.58%20%283%29.jpeg`,
  livingMirror: `${A}/eyquispp_WhatsApp%20Image%202026-07-22%20at%2001.43.59.jpeg`,
  bedMaking: `${A}/tlhpi125_WhatsApp%20Image%202026-07-22%20at%2001.45.41.jpeg`,
  bedroomLounge: `${A}/n4ie6moy_WhatsApp%20Image%202026-07-22%20at%2001.43.56%20%281%29.webp`,
  livingBlossom: `${A}/qk8dd67e_WhatsApp%20Image%202026-07-22%20at%2001.43.55.jpeg`,
  founderImg: `${A}/jwt02rcf_WhatsApp%20Image%202022-05-20%20at%2012.57.30%20AM.jpeg`,
};

const NAV = [
  { label: "Story", id: "story" },
  { label: "Founder", id: "founder" },
  { label: "Director", id: "director" },
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Contact", id: "contact" },
];

const downloadPDF = () => {
  const a = document.createElement("a");
  a.href = "/Studio-Hauz-Company-Profile.pdf";
  a.download = "Studio-Hauz-Company-Profile.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
};
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/* Small page label used on every A4 page */
const PageTag = ({ n, title }) => (
  <FadeUp className="flex items-baseline gap-4 mb-8">
    <span className="font-sans text-[11px] tracking-[0.3em] text-hauz-accent">PAGE {n} / 19</span>
    <span className="h-px w-14 bg-hauz-border" />
    <span className="text-[11px] uppercase tracking-[0.3em] text-hauz-secondary">{title}</span>
  </FadeUp>
);

const Shell = ({ id, tone = "bg", children, className = "" }) => (
  <section
    id={id}
    data-testid={`${id}-section`}
    className={`print-page ${tone === "surface" ? "bg-hauz-surface" : "bg-hauz-bg"} ${className}`}
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 w-full">{children}</div>
  </section>
);

/* ---------------- NAV ---------------- */
const Nav = () => (
  <header data-testid="site-header" className="no-print fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-hauz-surface/60 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
      <button data-testid="brand-logo" onClick={() => scrollTo("cover")} className="font-serif text-xl md:text-2xl tracking-tight text-hauz-primary">
        Studio Hauz<span className="text-hauz-accent">.</span>
      </button>
      <nav className="hidden md:flex items-center gap-8">
        {NAV.map((n) => (
          <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => scrollTo(n.id)}
            className="link-underline text-xs uppercase tracking-[0.2em] text-hauz-secondary hover:text-hauz-primary transition-colors duration-300">
            {n.label}
          </button>
        ))}
      </nav>
      <button data-testid="download-pdf-nav" onClick={downloadPDF}
        className="group inline-flex items-center gap-2 border border-hauz-accent text-hauz-accent hover:bg-hauz-accent hover:text-hauz-bg transition-colors duration-300 px-5 py-3 uppercase tracking-widest text-[10px] md:text-xs">
        <Download size={14} /><span className="hidden sm:inline">Download PDF</span>
      </button>
    </div>
  </header>
);

/* ---------------- 1. COVER ---------------- */
const Cover = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.12], [0, 100]);
  const scale = useTransform(scrollYProgress, [0, 0.12], [1, 1.08]);
  return (
    <section id="cover" data-testid="cover-section" className="print-page relative h-screen w-full overflow-hidden flex flex-col">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMG.hero} alt="Luxury interior by Studio Hauz" className="w-full h-full object-cover" />
        <video className="no-print absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline poster={IMG.hero} src={IMG.heroVideo} data-testid="hero-video" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-hauz-bg via-hauz-bg/25 to-hauz-bg/40" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pt-28 md:pt-32">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="font-serif text-2xl md:text-3xl tracking-tight text-hauz-primary print-reveal">
          Studio Hauz<span className="text-hauz-accent">.</span>
        </motion.p>
      </div>

      <div className="relative z-10 mt-auto max-w-7xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 1 }}
          className="text-xs md:text-sm uppercase tracking-[0.35em] text-hauz-accent mb-6 print-reveal">
          Company Profile — Est. 2017
        </motion.p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tighter text-hauz-primary">
          <RevealText lines={["Crafting Timeless", "Luxury Interiors"]} delay={0.3} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 text-base md:text-lg font-light text-hauz-secondary max-w-xl print-reveal">
          Bespoke turnkey interiors, luxury villas & handcrafted furniture — across Delhi, Gurgaon & five more cities, for eight years and counting.
        </motion.p>
      </div>
    </section>
  );
};

const Ribbon = () => (
  <div className="no-print border-y border-hauz-border bg-hauz-surface py-5">
    <Marquee speed={40} gradient={false} autoFill>
      {["Bespoke Interiors", "Turnkey Execution", "Luxury Villas", "Custom Furniture", "Delhi", "Gurgaon", "Noida", "Hyderabad", "Mumbai", "Vrindavan", "Raipur"].map((w, i) => (
        <span key={i} className="font-serif italic text-2xl md:text-3xl text-hauz-secondary mx-8 flex items-center gap-8">
          {w} <span className="text-hauz-accent not-italic text-lg">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------------- 2. BRAND STORY ---------------- */
const Story = () => (
  <Shell id="story">
    <PageTag n="02" title="Brand Story" />
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-7">
        <FadeUp>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-hauz-primary">
            We shape spaces that <span className="italic text-hauz-accent">live, breathe</span> and endure.
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-8 space-y-5 text-hauz-secondary font-light leading-relaxed">
          <p>
            Studio Hauz is a design consultancy working across commercial, hospitality and residential
            spaces. Since 2017 we have crafted homes and workplaces across Delhi, Gurgaon and cities beyond —
            growing from a small atelier into a full-service design house where architecture, interiors and
            furniture are conceived as one.
          </p>
          <p>
            Inspired by the golden ratio — a principle of proportion, balance and harmony — we create
            environments that feel intuitive, cohesive and timeless. Great interiors are not decorated,
            they are composed: every commission balances proportion, material honesty and the way people
            actually live — delivered turnkey, without compromise.
          </p>
        </FadeUp>
      </div>
      <div className="md:col-span-5">
        <FadeUp delay={0.15}>
          <div className="overflow-hidden border border-hauz-border">
            <img src={IMG.livingBlossom} alt="Studio Hauz living room" className="w-full h-[380px] md:h-[480px] object-cover hover-zoom" />
          </div>
        </FadeUp>
      </div>
    </div>
  </Shell>
);

/* ---------------- 3. VISION & MISSION ---------------- */
const VALUES = ["Proportion & Harmony", "Material Honesty", "Craftsmanship", "Transparency", "Timelessness"];
const Vision = () => (
  <Shell id="vision" tone="surface">
    <PageTag n="03" title="Vision & Mission" />
    <div className="grid md:grid-cols-2 gap-px bg-hauz-border border border-hauz-border">
      <FadeUp className="bg-hauz-surface p-8 md:p-12">
        <Compass className="text-hauz-accent" size={30} strokeWidth={1.2} />
        <h3 className="font-serif text-3xl md:text-4xl mt-8 text-hauz-primary">Our Vision</h3>
        <p className="mt-4 text-hauz-secondary font-light leading-relaxed">
          To be North India&apos;s most trusted design house — where every space, from private villa to
          workplace, tells a considered and timeless story rooted in balance and proportion.
        </p>
      </FadeUp>
      <FadeUp delay={0.1} className="bg-hauz-surface p-8 md:p-12">
        <Sparkles className="text-hauz-accent" size={30} strokeWidth={1.2} />
        <h3 className="font-serif text-3xl md:text-4xl mt-8 text-hauz-primary">Our Mission</h3>
        <p className="mt-4 text-hauz-secondary font-light leading-relaxed">
          To deliver seamless turnkey experiences and bespoke furniture that respect budget, timeline and
          craft in equal measure — with a single accountable team from concept to handover.
        </p>
      </FadeUp>
    </div>
    <FadeUp delay={0.15} className="mt-12">
      <p className="text-xs uppercase tracking-[0.3em] text-hauz-accent mb-6">Core Values</p>
      <div className="flex flex-wrap gap-3">
        {VALUES.map((v) => (
          <span key={v} className="border border-hauz-border text-hauz-primary px-5 py-2.5 text-sm tracking-wide hover:border-hauz-accent hover:text-hauz-accent transition-colors duration-300">{v}</span>
        ))}
      </div>
    </FadeUp>
  </Shell>
);

/* ---------------- 4. FOUNDER ---------------- */
const Founder = () => (
  <Shell id="founder">
    <PageTag n="04" title="Founder" />
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-5">
        <FadeUp>
          <div className="relative border border-hauz-border overflow-hidden">
            <img src={IMG.founderImg} alt="Studio Hauz signature interior" className="w-full h-[400px] md:h-[520px] object-cover hover-zoom" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-hauz-bg/90 to-transparent p-5">
              <div className="font-serif text-2xl text-hauz-primary">Chetna Khurana</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-hauz-secondary mt-1">Founder &amp; Principal Designer</div>
            </div>
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-7">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-hauz-accent mb-6">Founder&apos;s Desk</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-hauz-primary">
            Eight years of shaping <span className="italic text-hauz-accent">intentional</span> spaces.
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-8 space-y-5 text-hauz-secondary font-light leading-relaxed max-w-2xl">
          <p>
            Studio Hauz was founded by Chetna Khurana, whose eight years of practice span residential,
            commercial and hospitality interiors across Delhi, Gurgaon and beyond. Her work is guided by the
            golden ratio — proportion, balance and harmony — creating environments that feel intuitive and cohesive.
          </p>
          <p>
            With a strong focus on spatial planning, material balance and thoughtful detailing, every project
            is approached with clarity and intention — delivering spaces that are both functional and visually refined.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 flex items-center gap-4">
          <span className="font-serif italic text-2xl text-hauz-primary">Chetna Khurana</span>
          <span className="h-px w-12 bg-hauz-accent" />
          <span className="text-xs uppercase tracking-[0.2em] text-hauz-secondary">Founder, Studio Hauz</span>
        </FadeUp>
      </div>
    </div>
  </Shell>
);

/* ---------------- 5. DIRECTOR ---------------- */
const Director = () => (
  <Shell id="director" tone="surface">
    <PageTag n="05" title="Director's Desk" />
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-7">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-hauz-accent mb-6">Director&apos;s Desk</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-hauz-primary">
            Great design begins with <span className="italic text-hauz-accent">understanding people.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-8 space-y-4 text-hauz-secondary font-light leading-relaxed max-w-2xl">
          <p>
            At Studio Hauz, we believe that great design begins with understanding people. Every space has a
            story, and our role is to transform that story into an environment that is timeless, functional
            and deeply personal.
          </p>
          <p>
            Our design philosophy is rooted in simplicity, craftsmanship and thoughtful detail. We approach
            each project with a commitment to creating interiors that not only reflect our clients&apos; aspirations
            but also enhance the way they live, work and experience their spaces.
          </p>
          <p>
            From concept to completion, our team strives for excellence through innovation, precision and
            collaboration — every material, texture and element carefully curated to balance aesthetics and purpose.
          </p>
          <p>
            Thank you for placing your trust in Studio Hauz. We look forward to creating spaces that inspire,
            endure and leave a lasting impression.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-8 flex items-center gap-4">
          <span className="font-serif italic text-2xl text-hauz-primary">Umang Khurana</span>
          <span className="h-px w-12 bg-hauz-accent" />
          <span className="text-xs uppercase tracking-[0.2em] text-hauz-secondary">Director, Studio Hauz</span>
        </FadeUp>
      </div>
      <div className="md:col-span-5">
        <FadeUp>
          <div className="relative border border-hauz-border overflow-hidden">
            <img src={IMG.diningClassic} alt="Studio Hauz signature interior" className="w-full h-[400px] md:h-[520px] object-cover hover-zoom" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-hauz-bg/90 to-transparent p-5">
              <div className="font-serif text-2xl text-hauz-primary">Umang Khurana</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-hauz-secondary mt-1">Director</div>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </Shell>
);

/* ---------------- 6. WHY STUDIO HAUZ ---------------- */
const WHY = [
  { icon: PencilRuler, t: "Bespoke Design", d: "Every space is designed from first principles — never templated, always tailored to you." },
  { icon: Wrench, t: "Turnkey Execution", d: "One accountable team from drawings to handover — civil, joinery, furniture and styling." },
  { icon: Gem, t: "Premium Materials", d: "Marble, veneers, natural stone and brass, sourced and detailed to endure." },
  { icon: Eye, t: "Attention to Detail", d: "Proportion, alignment and finish — the small things that make a space feel considered." },
  { icon: ShieldCheck, t: "Transparent Process", d: "Clear scope, timelines and budgets — no surprises, from concept to completion." },
];
const Why = () => (
  <Shell id="why" tone="surface">
    <PageTag n="06" title="Why Studio Hauz" />
    <FadeUp>
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary max-w-3xl">
        Five reasons clients <span className="italic text-hauz-accent">trust us.</span>
      </h2>
    </FadeUp>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hauz-border border border-hauz-border">
      {WHY.map((w, i) => (
        <FadeUp key={w.t} delay={i * 0.07} className="bg-hauz-surface p-8 group">
          <w.icon className="text-hauz-accent" size={26} strokeWidth={1.3} />
          <h3 className="font-serif text-2xl md:text-3xl mt-6 text-hauz-primary">{w.t}</h3>
          <p className="mt-3 text-hauz-secondary font-light leading-relaxed">{w.d}</p>
        </FadeUp>
      ))}
      <FadeUp delay={0.35} className="bg-hauz-bg p-8 flex items-center justify-between">
        <span className="font-serif text-5xl text-hauz-accent">08</span>
        <span className="text-xs uppercase tracking-[0.2em] text-hauz-secondary text-right">Years of<br />Excellence</span>
      </FadeUp>
    </div>
  </Shell>
);

/* ---------------- 6. SERVICES ---------------- */
const SERVICES = [
  { icon: Home, t: "Residential Interiors" },
  { icon: Building2, t: "Luxury Villas" },
  { icon: Layers, t: "Apartments" },
  { icon: Building2, t: "Commercial Interiors" },
  { icon: PencilRuler, t: "Office Design" },
  { icon: Sofa, t: "Furniture Design" },
  { icon: Hammer, t: "Turnkey Execution" },
];
const Services = () => (
  <Shell id="services">
    <PageTag n="07" title="Our Services" />
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-5">
        <FadeUp>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary leading-[0.95]">
            A full spectrum of <span className="italic text-hauz-accent">design services.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="mt-8 overflow-hidden border border-hauz-border">
            <img src={IMG.livingMirror} alt="Studio Hauz interior" className="w-full h-[280px] md:h-[340px] object-cover hover-zoom" />
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-7">
        <div className="grid sm:grid-cols-2 gap-px bg-hauz-border border border-hauz-border">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.t} delay={i * 0.05} className="bg-hauz-bg p-6 flex items-center gap-4 group">
              <s.icon className="text-hauz-accent shrink-0" size={22} strokeWidth={1.3} />
              <span className="font-serif text-xl md:text-2xl text-hauz-primary">{s.t}</span>
              <ArrowUpRight className="ml-auto text-hauz-border group-hover:text-hauz-accent transition-colors duration-300" size={18} />
            </FadeUp>
          ))}
          <FadeUp delay={0.4} className="bg-hauz-surface p-6 flex items-center">
            <span className="text-sm text-hauz-secondary font-light">…and everything in between, delivered end-to-end.</span>
          </FadeUp>
        </div>
      </div>
    </div>
  </Shell>
);

/* ---------------- 7. DESIGN PROCESS ---------------- */
const STEPS = [
  { n: "01", t: "Consultation", d: "We listen — lifestyle, budget, timeline and taste." },
  { n: "02", t: "Concept Design", d: "Mood, direction and 3D visuals for a shared vision." },
  { n: "03", t: "Space Planning", d: "Layouts optimised for flow, function and proportion." },
  { n: "04", t: "Material Selection", d: "Marble, veneers, stone, brass and finishes curated." },
  { n: "05", t: "Execution", d: "Civil works, joinery and in-house furniture in motion." },
  { n: "06", t: "Handover", d: "Final styling, quality checks and a turnkey handover." },
];
const Process = () => (
  <Shell id="process" tone="surface">
    <PageTag n="08" title="Our Design Process" />
    <FadeUp>
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary max-w-3xl">
        Six steps, <span className="italic text-hauz-accent">seamlessly</span> managed.
      </h2>
    </FadeUp>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hauz-border border border-hauz-border">
      {STEPS.map((s, i) => (
        <FadeUp key={s.n} delay={i * 0.06} className="bg-hauz-surface p-8">
          <div className="font-serif text-5xl text-hauz-accent">{s.n}</div>
          <h3 className="mt-4 text-hauz-primary text-xl font-medium">{s.t}</h3>
          <p className="mt-2 text-sm text-hauz-secondary font-light leading-relaxed">{s.d}</p>
        </FadeUp>
      ))}
    </div>
  </Shell>
);

/* ---------------- 8-14. PORTFOLIO (7 projects) ---------------- */
const PROJECTS = [
  { id: "portfolio", name: "The Curved Sofa Residence", hero: IMG.hero, imgs: [IMG.living2, IMG.bedroom],
    location: "Gurgaon", scope: "Full Turnkey Interiors", area: "3,200 sq.ft", style: "Contemporary Luxury", year: "2024" },
  { id: "project-2", name: "Blossom House", hero: IMG.livingBlossom, imgs: [IMG.bedroomClassic, IMG.marbleInlay],
    location: "Delhi", scope: "Residential + Furniture", area: "4,000 sq.ft", style: "Modern Classic", year: "2023" },
  { id: "project-3", name: "The Mirror Apartment", hero: IMG.livingMirror, imgs: [IMG.desk, IMG.bedMaking],
    location: "Noida", scope: "Interiors + Custom Furniture", area: "2,800 sq.ft", style: "Art Deco Glam", year: "2024" },
  { id: "project-4", name: "Heritage Suite", hero: IMG.bedroomLounge, imgs: [IMG.diningClassic, IMG.bar],
    location: "Hyderabad", scope: "Luxury Villa Turnkey", area: "6,500 sq.ft", style: "Neoclassical", year: "2022" },
  { id: "project-5", name: "Warm Minimal Villa", hero: IMG.bedroomRender, imgs: [IMG.render, IMG.barRender],
    location: "Mumbai", scope: "Design + Visualisation", area: "5,000 sq.ft", style: "Warm Minimalism", year: "2025" },
  { id: "project-6", name: "The Home Office", hero: IMG.studio, imgs: [IMG.desk, IMG.dining],
    location: "Gurgaon", scope: "Home Office & Study", area: "3,500 sq.ft", style: "Modern Workspace", year: "2023" },
  { id: "project-7", name: "The Grand Bar & Lounge", hero: IMG.bar, imgs: [IMG.barRender, IMG.diningClassic],
    location: "Vrindavan", scope: "Hospitality Interiors", area: "2,000 sq.ft", style: "Classical Revival", year: "2024" },
];
const ProjectPage = ({ p, index }) => (
  <Shell id={p.id} className="proj-page">
    <PageTag n={String(9 + index).padStart(2, "0")} title={`Portfolio — ${index + 1} of 7`} />
    <FadeUp>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary leading-[0.95]">{p.name}</h2>
        <span className="text-xs uppercase tracking-[0.25em] text-hauz-accent">{p.style}</span>
      </div>
    </FadeUp>
    <FadeUp delay={0.08}>
      <div className="overflow-hidden border border-hauz-border">
        <img src={p.hero} alt={p.name} className="w-full h-[300px] md:h-[440px] object-cover hover-zoom" />
      </div>
    </FadeUp>
    <div className="mt-6 grid md:grid-cols-12 gap-6">
      <div className="md:col-span-8 grid grid-cols-2 gap-4 md:gap-6">
        {p.imgs.map((src, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div className="overflow-hidden border border-hauz-border h-[180px] md:h-[220px]">
              <img src={src} alt={`${p.name} detail ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover-zoom" />
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.12} className="md:col-span-4 border border-hauz-border p-6 md:p-8 bg-hauz-surface">
        <p className="text-[11px] uppercase tracking-[0.3em] text-hauz-accent mb-6">Project Details</p>
        {[
          ["Location", p.location], ["Scope", p.scope], ["Area", p.area],
          ["Style", p.style], ["Completion", p.year],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 py-3 border-b border-hauz-border last:border-0">
            <span className="text-xs uppercase tracking-[0.15em] text-hauz-secondary">{k}</span>
            <span className="text-sm text-hauz-primary text-right font-light">{v}</span>
          </div>
        ))}
      </FadeUp>
    </div>
  </Shell>
);

/* ---------------- 15. MATERIALS & CRAFTSMANSHIP ---------------- */
const MATERIALS = ["Marble", "Veneers", "Natural Stones", "Brass", "Upholstery", "Custom Furniture", "Lighting"];
const Materials = () => (
  <Shell id="materials" tone="surface">
    <PageTag n="16" title="Materials & Craftsmanship" />
    <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <div>
        <FadeUp>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary leading-[0.95]">
            Honest materials, <span className="italic text-hauz-accent">masterful craft.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-6 text-hauz-secondary font-light leading-relaxed max-w-lg">
            From marble inlay floors to bespoke joinery and in-house furniture, every material is chosen for
            beauty and longevity — then executed by our own artisans with obsessive attention to detail.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-8 flex flex-wrap gap-3">
          {MATERIALS.map((m) => (
            <span key={m} className="border border-hauz-border text-hauz-primary px-5 py-2.5 text-sm tracking-wide hover:border-hauz-accent hover:text-hauz-accent transition-colors duration-300">{m}</span>
          ))}
        </FadeUp>
      </div>
      <div className="grid grid-cols-2 gap-4 md:gap-6">
        {[IMG.marbleInlay, IMG.floorInlay, IMG.bedMaking, IMG.processBath].map((src, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div className="overflow-hidden border border-hauz-border h-[160px] md:h-[220px]">
              <img src={src} alt={`Craft detail ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover-zoom" />
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </Shell>
);

/* ---------------- 16. TESTIMONIALS ---------------- */
const TESTIMONIALS = [
  { q: "Studio Hauz turned our apartment into a home that feels effortless and elegant. Their turnkey process meant we never had to chase a single vendor.", a: "R. Malhotra", c: "Residence, Gurgaon" },
  { q: "The in-house furniture is exquisite — every piece fits the space perfectly. Craftsmanship you can feel.", a: "A. & S. Kapoor", c: "Apartment, Delhi" },
  { q: "Professional, transparent and on time. Rare in this industry. Our office reflects exactly who we are.", a: "Managing Director", c: "Corporate Client, Noida" },
  { q: "Chetna's eye for proportion is unmatched. Our villa feels like a timeless masterpiece.", a: "V. Reddy", c: "Villa, Hyderabad" },
];
const Testimonials = () => (
  <Shell id="testimonials">
    <PageTag n="17" title="Testimonials" />
    <FadeUp>
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary max-w-3xl">
        Words from <span className="italic text-hauz-accent">our clients.</span>
      </h2>
    </FadeUp>
    <div className="mt-12 grid md:grid-cols-2 gap-px bg-hauz-border border border-hauz-border">
      {TESTIMONIALS.map((t, i) => (
        <FadeUp key={i} delay={i * 0.08} className="bg-hauz-bg p-8 md:p-10">
          <Quote className="text-hauz-accent" size={28} />
          <p className="mt-5 font-serif text-xl md:text-2xl italic text-hauz-primary leading-snug">“{t.q}”</p>
          <div className="mt-6 flex items-center gap-1 text-hauz-accent">
            {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
          </div>
          <div className="mt-4">
            <div className="text-hauz-primary font-medium">{t.a}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-hauz-secondary mt-1">{t.c}</div>
          </div>
        </FadeUp>
      ))}
    </div>
  </Shell>
);

/* ---------------- 17. TRUSTED PARTNERS ---------------- */
const PARTNERS = [
  { t: "Vendors & Suppliers", items: ["Marble & Stone", "Modular Kitchens", "Sanitaryware", "Glass & Mirror"] },
  { t: "Material Brands", items: ["Premium Veneers", "Designer Laminates", "Brass Hardware", "Imported Tiles"] },
  { t: "Consultants", items: ["Structural", "MEP / Electrical", "Lighting Design", "Landscape"] },
  { t: "Contractors", items: ["Civil & Masonry", "Carpentry", "False Ceiling", "Painting & Polish"] },
];
const Partners = () => (
  <Shell id="partners" tone="surface">
    <PageTag n="18" title="Trusted Partners" />
    <FadeUp>
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary max-w-3xl">
        A network built on <span className="italic text-hauz-accent">trust.</span>
      </h2>
    </FadeUp>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-hauz-border border border-hauz-border">
      {PARTNERS.map((p, i) => (
        <FadeUp key={p.t} delay={i * 0.07} className="bg-hauz-surface p-7">
          <h3 className="font-serif text-2xl text-hauz-primary">{p.t}</h3>
          <ul className="mt-5 space-y-2">
            {p.items.map((it) => (
              <li key={it} className="text-sm text-hauz-secondary font-light flex items-center gap-2">
                <span className="w-1 h-1 bg-hauz-accent rounded-full" />{it}
              </li>
            ))}
          </ul>
        </FadeUp>
      ))}
    </div>
    <FadeUp delay={0.3}><p className="mt-6 text-xs text-hauz-secondary font-light italic">Representative categories — full partner list available on request.</p></FadeUp>
  </Shell>
);

/* ---------------- 18. CONTACT ---------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  const igQr = "https://api.qrserver.com/v1/create-qr-code/?size=240x240&bgcolor=0A0A0B&color=9E8569&qzone=2&data=https://instagram.com/studiohauz.in";
  return (
    <section id="contact" data-testid="contact-section" className="print-page bg-hauz-bg border-t border-hauz-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 w-full">
        <PageTag n="19" title="Contact" />
        <div className="grid md:grid-cols-2 gap-14 md:gap-20">
          <div>
            <FadeUp>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-hauz-primary leading-[0.9]">
                Let&apos;s create spaces that <span className="italic text-hauz-accent">inspire, endure</span> and define luxury.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1} className="mt-10 space-y-5">
              <a data-testid="contact-phone" href="tel:+918198905441" className="flex items-center gap-4 text-hauz-primary">
                <Phone className="text-hauz-accent" size={20} /><span className="link-underline text-lg font-light">+91 81989 05441</span>
              </a>
              <a data-testid="contact-email" href="mailto:chetna@studiohauz.in" className="flex items-center gap-4 text-hauz-primary">
                <Mail className="text-hauz-accent" size={20} /><span className="link-underline text-lg font-light">chetna@studiohauz.in</span>
              </a>
              <a data-testid="contact-web" href="https://www.studiohauz.in" target="_blank" rel="noreferrer" className="flex items-center gap-4 text-hauz-primary">
                <Globe className="text-hauz-accent" size={20} /><span className="link-underline text-lg font-light">www.studiohauz.in</span>
              </a>
              <a data-testid="contact-ig" href="https://instagram.com/studiohauz.in" target="_blank" rel="noreferrer" className="flex items-center gap-4 text-hauz-primary">
                <Instagram className="text-hauz-accent" size={20} /><span className="link-underline text-lg font-light">@studiohauz.in</span>
              </a>
              <div className="flex items-start gap-4 text-hauz-primary">
                <MapPin className="text-hauz-accent mt-1" size={20} /><span className="text-lg font-light">Delhi · Gurgaon · Noida · Hyderabad · Mumbai · Vrindavan · Raipur</span>
              </div>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-10 flex items-center gap-5">
              <img src={igQr} alt="Scan for Studio Hauz on Instagram" className="w-28 h-28 border border-hauz-border bg-hauz-surface p-1.5" data-testid="contact-ig-qr" />
              <span className="text-xs uppercase tracking-[0.2em] text-hauz-secondary flex items-center gap-2">
                <Instagram size={14} className="text-hauz-accent" />Scan to follow<br />@studiohauz.in
              </span>
            </FadeUp>
          </div>

          <FadeUp delay={0.1} className="no-print">
            <form onSubmit={submit} data-testid="enquiry-form" className="border border-hauz-border p-8 md:p-10 space-y-6 bg-hauz-surface">
              <p className="text-xs uppercase tracking-[0.2em] text-hauz-secondary">Start an enquiry</p>
              {["name", "email"].map((f) => (
                <div key={f}>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-hauz-secondary mb-2">{f}</label>
                  <input data-testid={`enquiry-${f}`} type={f === "email" ? "email" : "text"} value={form[f]}
                    onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    className="w-full bg-transparent border-b border-hauz-border focus:border-hauz-accent outline-none py-3 text-hauz-primary font-light transition-colors duration-300" />
                </div>
              ))}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-hauz-secondary mb-2">Message</label>
                <textarea data-testid="enquiry-message" rows={3} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-hauz-border focus:border-hauz-accent outline-none py-3 text-hauz-primary font-light resize-none transition-colors duration-300" />
              </div>
              <button data-testid="enquiry-submit" type="submit" disabled={loading}
                className="w-full border border-hauz-accent text-hauz-accent hover:bg-hauz-accent hover:text-hauz-bg transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-xs disabled:opacity-50">
                {loading ? "Sending…" : "Send Enquiry"}
              </button>
              <button type="button" data-testid="download-pdf-connect" onClick={downloadPDF}
                className="w-full inline-flex items-center justify-center gap-2 text-hauz-secondary hover:text-hauz-accent transition-colors duration-300 text-xs uppercase tracking-widest pt-2">
                <Download size={14} /> Download Company Profile
              </button>
            </form>
          </FadeUp>
        </div>

        <FadeUp delay={0.2} className="mt-20 pt-8 border-t border-hauz-border flex flex-col md:flex-row justify-between gap-4 text-xs text-hauz-secondary uppercase tracking-[0.2em]">
          <span className="font-serif text-lg tracking-tight text-hauz-primary normal-case">Studio Hauz Interiors</span>
          <span>© {new Date().getFullYear()} — All rights reserved</span>
        </FadeUp>
      </div>
    </section>
  );
};

/* ---------------- PAGE ---------------- */
export default function Profile() {
  return (
    <main data-testid="profile-page">
      <Nav />
      <Cover />
      <Ribbon />
      <Story />
      <Vision />
      <Founder />
      <Director />
      <Why />
      <Services />
      <Process />
      {PROJECTS.map((p, i) => <ProjectPage key={p.id} p={p} index={i} />)}
      <Materials />
      <Testimonials />
      <Partners />
      <Contact />
    </main>
  );
}
